import React, { ReactElement, useState } from "react";
import Button from "@mui/material/Button";
import { SxProps, Theme } from "@mui/material/styles";
import useDownloadFile from "../../hooks/useDownloadFile";
import useAnnotatedDownloadFile from "../../hooks/useAnnotatedDownloadFile";
import { useUserContext } from "../../providers/useUserProvider";
import DownloadIcon from "@mui/icons-material/Download";
import {
    Box,
    ButtonGroup,
    CircularProgress,
    ClickAwayListener,
    Grow,
    IconButton,
    MenuItem,
    MenuList,
    Paper,
    Popper,
    Tooltip,
    Typography,
} from "@mui/material";
import { ArrowDropDownIcon } from "@mui/x-date-pickers";

interface ErrorState {
    hasError: boolean;
    message: string;
}

interface DownloadButtonBaseProps {
    /**
     * DeviceID to use for querying a file to download
     */
    deviceId: string;

    /**
     * Timestamp to use for querying a file to download
     */
    timestamp: Date;

    /**
     * Custom filename for the downloaded file
     */
    filename: string;

    /**
     * Callback function called when download fails
     */
    setErrorState: (state: ErrorState) => void;

    /**
     * Whether to render the built-in loading state
     * @default true
     */
    showLoadingState?: boolean;
}

interface DefaultButtonProps extends DownloadButtonBaseProps {
    /**
     * Custom render function for the button
     * If not provided, a default MUI Button will be used
     */
    buttonComponent?: never;

    /**
     * Text label for the button
     * @default "Download"
     */
    label?: string;

    /**
     * Text shown during download
     * @default "Downloading..."
     */
    loadingLabel?: string;

    /**
     * MUI Button variant
     * @default "contained"
     */
    variant?: "text" | "outlined" | "contained";

    /**
     * MUI Button color
     * @default "primary"
     */
    color?:
        | "inherit"
        | "primary"
        | "secondary"
        | "success"
        | "error"
        | "info"
        | "warning";

    /**
     * MUI SX props for additional styling
     */
    sx?: SxProps<Theme>;

    /**
     * Button size
     * @default "medium"
     */
    size?: "small" | "medium" | "large";
}

interface CustomButtonProps extends DownloadButtonBaseProps {
    /**
     * Custom render function for the button
     * Receives download state and handler
     */
    buttonComponent: (props: {
        onClick: () => void;
        isDownloading: boolean;
        disabled: boolean;
    }) => ReactElement;
    loadingLabel?: never;
    variant?: never;
    color?: never;
    sx?: never;
    size?: never;
}

type DownloadButtonProps = DefaultButtonProps | CustomButtonProps;

/**
 * DownloadButton Component
 *
 * A flexible download button that can use either the default MUI Button
 * or a custom button component provided by a higher level component.
 */
const DownloadButton: React.FC<DownloadButtonProps> = (props) => {
    const {
        deviceId,
        timestamp,
        filename,
        setErrorState,
        showLoadingState = true,
    } = props;

    const downloadMutation = useDownloadFile({ setErrorState: setErrorState });
    const downloadAnnotatedMutation = useAnnotatedDownloadFile({
        setErrorState: setErrorState,
    });
    const [isDownloading, setIsDownloading] = useState(false);
    const [open, setOpen] = React.useState(false);
    const anchorRef = React.useRef<HTMLDivElement>(null);
    const [selectedIndex, setSelectedIndex] = React.useState(0);
    const { isInternalUser } = useUserContext();
    const options = ["Download", "Centroid", "Bounding Box"];
    const filenameExt = ".mp4";
    const lineWidth = 2,
        radius = 5;

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (event: Event) => {
        if (
            anchorRef.current &&
            anchorRef.current.contains(event.target as HTMLElement)
        ) {
            return;
        }

        setOpen(false);
    };

    const handleDownloads = (index: number) => {
        setSelectedIndex(index);
        setOpen(false);
        const option = options[index];

        switch (option) {
            case options[0]:
                handleDownloadRegularVideo();
                break;
            case options[1]:
                handleDownloadCentroidVideo();
                break;
            case options[2]:
                handleDownloadBBoxVideo();
                break;
            default:
                break;
        }
    };

    const handleDownloadRegularVideo = () => {
        setIsDownloading(true);
        downloadMutation.mutate(
            {
                deviceId: deviceId,
                timestamp: timestamp.toISOString(),
                filename: `${filename}${filenameExt}`,
            },
            {
                onSettled: () => {
                    setIsDownloading(false);
                },
            },
        );
    };

    const handleDownloadCentroidVideo = () => {
        setIsDownloading(true);
        downloadAnnotatedMutation.mutate(
            {
                deviceId: deviceId,
                timestamp: timestamp.toISOString(),
                filename: `${filename}-centroid${filenameExt}`,
                requestBody: {
                    classConfigs: {
                        "full-fish": {
                            shape: "centroid",
                            color: "#FF0000",
                            lineWidth: lineWidth,
                            radius: radius,
                        },
                    },
                    defaultConfig: {
                        shape: "centroid",
                        color: "#0000FF",
                        lineWidth: lineWidth,
                        radius: radius,
                    },
                    skipUnconfigured: false,
                },
            },
            {
                onSettled: () => {
                    setIsDownloading(false);
                },
            },
        );
    };

    const handleDownloadBBoxVideo = () => {
        setIsDownloading(true);
        downloadAnnotatedMutation.mutate(
            {
                deviceId: deviceId,
                timestamp: timestamp.toISOString(),
                filename: `${filename}-bbox${filenameExt}`,
                requestBody: {
                    classConfigs: {
                        "full-fish": {
                            shape: "box",
                            color: "#FF0000",
                            lineWidth: lineWidth,
                        },
                    },
                    defaultConfig: {
                        shape: "box",
                        color: "#0000FF",
                        lineWidth: lineWidth,
                    },
                    skipUnconfigured: false,
                },
            },
            {
                onSettled: () => {
                    setIsDownloading(false);
                },
            },
        );
    };

    // If a custom button component is provided, use it
    if ("buttonComponent" in props && props.buttonComponent) {
        return props.buttonComponent({
            onClick: handleDownloadRegularVideo,
            isDownloading,
            disabled: isDownloading,
        });
    }

    // Otherwise, use the default MUI Button implementation
    const {
        label = "Download",
        loadingLabel = "Downloading...",
        variant = "contained",
        color = "primary",
        sx,
        size = "medium",
    } = props as DefaultButtonProps;

    return (
        <>
            {isInternalUser() ? (
                <>
                    <ButtonGroup
                        variant={variant}
                        ref={anchorRef}
                        aria-label="Button group with a nested menu"
                    >
                        <Button
                            aria-label={label}
                            onClick={() => handleDownloads(selectedIndex)}
                        >
                            {isDownloading ? (
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    <CircularProgress
                                        size={24}
                                        sx={{ color: "inherit" }}
                                    />
                                    <Typography
                                        variant="button"
                                        component="span"
                                        sx={{
                                            position: "relative",
                                            left: "10px",
                                        }}
                                    >
                                        {loadingLabel}
                                    </Typography>
                                </Box>
                            ) : (
                                <Typography variant="button" component="span">
                                    {options[selectedIndex]}
                                </Typography>
                            )}
                        </Button>
                        <Button
                            color={color}
                            onClick={handleToggle}
                            size="small"
                        >
                            <ArrowDropDownIcon />
                        </Button>
                    </ButtonGroup>
                    <Popper
                        open={open}
                        sx={{ zIndex: 9999 }}
                        anchorEl={anchorRef.current}
                        transition
                        container={document.body}
                    >
                        {({ TransitionProps, placement }) => (
                            <Grow
                                {...TransitionProps}
                                style={{
                                    transformOrigin:
                                        placement === "bottom"
                                            ? "center top"
                                            : "center bottom",
                                }}
                            >
                                <Paper>
                                    <ClickAwayListener
                                        onClickAway={handleClose}
                                    >
                                        <MenuList
                                            id="split-button-menu"
                                            autoFocusItem
                                        >
                                            {options.map((option, index) => (
                                                <MenuItem
                                                    key={index}
                                                    selected={
                                                        index === selectedIndex
                                                    }
                                                    onClick={() =>
                                                        handleDownloads(index)
                                                    }
                                                >
                                                    {option}
                                                </MenuItem>
                                            ))}
                                        </MenuList>
                                    </ClickAwayListener>
                                </Paper>
                            </Grow>
                        )}
                    </Popper>
                </>
            ) : (
                <Tooltip title={`Download ${filename}.mp4`}>
                    <span>
                        <IconButton
                            aria-label="Download"
                            size="small"
                            sx={{
                                background: "#4eafb2",
                                "&:hover": {
                                    backgroundColor: "#043c4a",
                                    boxShadow: "none",
                                },
                            }}
                            disabled={isDownloading}
                            onClick={handleDownloadRegularVideo}
                        >
                            {isDownloading ? (
                                <CircularProgress size={24} />
                            ) : (
                                <DownloadIcon />
                            )}
                        </IconButton>
                    </span>
                </Tooltip>
            )}
        </>
    );
};

export default DownloadButton;
